import "./MyCourse.css";
import { useState, useEffect, useMemo } from "react";
import { IoIosSearch } from "react-icons/io";
import { Book, Calendar, Clock } from "iconsax-reactjs";
import { fetchUserEnrolledCohorts } from "../../../../redux/slices/userEnrolledCohortSlice";
import { fetchUserProgress } from "../../../../redux/slices/progressSlice";
import { deduplicateEnrollments } from "../../../../utils/enrollmentUtils";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import CourseSVG from "../../../cohort/Pencil";

const MyCourse = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();

  const { enrollment, loading: enrolledLoading, error } = useSelector(
    (state) => state.userEnrolledCohorts
  );
  const { progressData, loading: progressLoading } = useSelector(
    (state) => state.progress
  );

  const loading = enrolledLoading || progressLoading;

  const isOrg = window.location.pathname.startsWith("/organization");
  const routePrefix = isOrg ? "/organization" : "";

  // Fetch enrolled cohorts and progress data
  useEffect(() => {
    dispatch(fetchUserEnrolledCohorts());
    dispatch(fetchUserProgress());
  }, [dispatch]);

  // Combine and deduplicate across endpoints
  const allEnrollments = useMemo(() => {
    return deduplicateEnrollments(enrollment, progressData);
  }, [enrollment, progressData]);

  // Filter based on search query
  const filteredEnrollments = useMemo(() => {
    if (!allEnrollments || allEnrollments.length === 0) return [];

    return allEnrollments.filter((item) => {
      const title = (
        item.course?.title ??
        item.cohort?.course?.title ??
        item.cohort?.name ??
        item.title ??
        ""
      ).toLowerCase();

      const category = (item.course?.category || "").toLowerCase();
      const query = searchTerm.trim().toLowerCase();

      return query === "" || title.includes(query) || category.includes(query);
    });
  }, [allEnrollments, searchTerm]);

  const truncateString = (str, num = 120) => {
    if (!str) return "";
    const clean = str.replace(/<[^>]*>?/gm, "");
    if (clean.length <= num) return clean;
    return clean.slice(0, num) + "...";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="my-courses-page">
      {/* Responsive Hero Header */}
      <section className="my-courses-hero">
        <div className="my-courses-hero-inner">
          <div className="my-courses-hero-content">
            <h1 className="my-courses-hero-title">My Enrolled Courses</h1>
            <p className="my-courses-hero-subtitle">
              Track and manage all your enrolled programs, access course materials,
              and review your ongoing learning progress in one central hub.
            </p>
          </div>
          <div className="my-courses-hero-svg" aria-hidden="true">
            <CourseSVG width="10rem" height="10rem" />
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="my-courses-container">
        {/* Search & Counter Controls */}
        <div className="my-courses-controls">
          <div className="my-courses-search">
            <IoIosSearch className="my-courses-search-icon" />
            <input
              type="text"
              className="my-courses-search-input"
              placeholder="Search enrolled courses by title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search enrolled courses"
            />
          </div>

          <span className="my-courses-count-badge">
            {filteredEnrollments.length} {filteredEnrollments.length === 1 ? "Course" : "Courses"}
          </span>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="my-courses-error-box" role="alert">
            {typeof error === "string" ? error : "Failed to load your enrolled courses."}
          </div>
        )}

        {/* Loading Spinner */}
        {loading && allEnrollments.length === 0 ? (
          <div className="my-courses-spinner-wrapper">
            <div className="spinner" role="status" aria-label="Loading enrolled courses..." />
          </div>
        ) : filteredEnrollments.length === 0 ? (
          /* Empty State */
          <div className="my-courses-empty">
            <Book size={44} color="#94a3b8" style={{ marginBottom: "12px" }} />
            <h3>No enrolled courses found</h3>
            <p>
              {searchTerm
                ? `No courses matched "${searchTerm}". Try another keyword.`
                : "You have not enrolled in any training programs yet."}
            </p>
            <Link to={`${routePrefix}/cohorts`} className="my-course-btn my-course-btn-primary" style={{ display: "inline-flex", maxWidth: "200px", margin: "0 auto" }}>
              Explore Courses
            </Link>
          </div>
        ) : (
          /* Responsive Courses Grid */
          <div className="my-courses-grid">
            {filteredEnrollments.map((cohort, index) => {
              const isPrivate = cohort.class_option === "Private Class";
              const itemCohort = cohort.cohort || {};
              const courseTitle = isPrivate
                ? (cohort.course?.title ?? itemCohort?.course?.title ?? "Private Class Program")
                : (itemCohort?.name || cohort.course?.title || "Cohort Program");

              const thumbnail = cohort.course?.thumbnail_path || itemCohort?.course?.thumbnail_path;
              const description = cohort.course?.description || itemCohort?.course?.description || "";
              const cohortSlug = itemCohort?.slug;
              const displayDuration = isPrivate
                ? cohort.duration || "Self-Paced"
                : itemCohort?.duration || "Multi-Week";

              const progressVal = Math.min(100, Math.max(0, Number(cohort.progress) || 0));

              const detailsLink = cohortSlug
                ? isOrg
                  ? `/organization/cohorts/${cohortSlug}`
                  : `/cohorts/${cohortSlug}/details`
                : null;

              const progressLink = itemCohort?.id
                ? `${routePrefix}/progress/cohort/${itemCohort.id}/assignments`
                : (cohort.enrollment_id || cohort.id)
                ? `${routePrefix}/progress/enrollment/${cohort.enrollment_id || cohort.id}/assignments`
                : `${routePrefix}/progress`;

              const calendarLink = !isPrivate && itemCohort?.start_date && itemCohort?.end_date
                ? `${routePrefix}/calendar/${itemCohort.start_date}/${itemCohort.end_date}`
                : null;

              return (
                <div key={cohort.id || cohort.enrollment_id || index} className="my-course-card">
                  {/* Thumbnail / Header */}
                  <div className="my-course-thumb-wrapper">
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt={courseTitle}
                        className="my-course-thumb"
                        loading="lazy"
                      />
                    ) : (
                      <div className="my-course-thumb-placeholder">
                        <Book size={38} color="#94a3b8" />
                      </div>
                    )}
                    <span className={`my-course-badge ${isPrivate ? "private" : "cohort"}`}>
                      {isPrivate ? "Private Class" : (cohort.class_option || "Cohort")}
                    </span>
                  </div>

                  {/* Card Content */}
                  <div className="my-course-body">
                    <h2 className="my-course-title">{courseTitle}</h2>
                    <p className="my-course-desc">{truncateString(description, 110)}</p>

                    {/* Progress Bar */}
                    <div className="my-course-progress-section">
                      <div className="my-course-progress-header">
                        <span>Course Progress</span>
                        <span>{progressVal}%</span>
                      </div>
                      <div className="my-course-progress-track">
                        <div
                          className="my-course-progress-fill"
                          role="progressbar"
                          style={{ width: `${progressVal}%` }}
                          aria-valuenow={progressVal}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        />
                      </div>
                    </div>

                    {/* Metadata Row */}
                    <div className="my-course-meta-row">
                      <div className="my-course-meta-item">
                        <Clock size={15} color="#94a3b8" />
                        <span><strong>Duration:</strong> {displayDuration}</span>
                      </div>
                      {cohort.enrolled_at && (
                        <div className="my-course-meta-item">
                          <Calendar size={15} color="#94a3b8" />
                          <span><strong>Enrolled:</strong> {formatDate(cohort.enrolled_at)}</span>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="my-course-actions">
                      <Link to={progressLink} className="my-course-btn my-course-btn-primary">
                        View Progress
                      </Link>

                      {detailsLink && (
                        <Link to={detailsLink} className="my-course-btn my-course-btn-outline">
                          Details
                        </Link>
                      )}

                      {calendarLink && (
                        <Link to={calendarLink} className="my-course-btn my-course-btn-outline">
                          Calendar
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCourse;
